import { useQuery } from '@tanstack/react-query';
import { pb, Booking } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';

export const useBookings = () => {
  const { user } = useAuth();
  
  const query = useQuery({
    queryKey: ['bookings', user?.id, user?.role],
    queryFn: async ({ signal }) => {
      // Early abort check
      if (signal?.aborted) {
        return [];
      }

      if (!user?.id) {
        console.log('No user ID available for bookings query');
        return [];
      }
      
      console.log('Fetching bookings for user:', user.id, 'with role:', user?.role);
      
      try {
        let bookings: any[] = [];
        
        if (user?.role === 'tutor') {
          // Check if request was cancelled
          if (signal?.aborted) {
            return [];
          }
          
          // For tutors, get their tutor profile first, then find bookings using the profile ID
          const tutorProfiles = await pb.collection('tutors').getList(1, 1, {
            filter: `user="${user.id}"`,
            requestKey: `tutor-profile-${user.id}-${Date.now()}`,
            signal
          });
          
          if (tutorProfiles.items.length === 0) {
            return [];
          }
          
          const tutorProfileId = tutorProfiles.items[0].id;
          
          // Check if request was cancelled before fetching bookings
          if (signal?.aborted) {
            return [];
          }
          
          // Fetch bookings where this tutor is teaching (tutorId)
          const teachingBookingsPromise = pb.collection('bookings').getList(1, 50, {
            filter: `tutorId="${tutorProfileId}"`,
            sort: '-created',
            expand: 'studentId,tutorId',
            requestKey: `tutor-teaching-${tutorProfileId}-${Date.now()}`,
            signal
          });
          
          // Fetch bookings where this tutor is the student (studentId)
          const learningBookingsPromise = pb.collection('bookings').getList(1, 50, {
            filter: `studentId="${user.id}"`,
            sort: '-created',
            expand: 'studentId,tutorId',
            requestKey: `tutor-learning-${user.id}-${Date.now()}`,
            signal
          });
          
          // Wait for both queries to complete
          const [teachingBookings, learningBookings] = await Promise.all([
            teachingBookingsPromise,
            learningBookingsPromise
          ]);
          
          // Combine and deduplicate bookings
          const allBookings = [...teachingBookings.items, ...learningBookings.items];
          bookings = allBookings.filter((booking, index, self) => 
            index === self.findIndex(b => b.id === booking.id)
          );
        } else {
          // For students, use the user ID directly (studentId references user ID)
          
          // Check if request was cancelled
          if (signal?.aborted) {
            return [];
          }
          
          // Fetch bookings for this student (as leader)
          const leaderBookingsPromise = pb.collection('bookings').getList(1, 50, {
            filter: `studentId="${user.id}"`,
            sort: '-created',
            expand: 'studentId,tutorId',
            requestKey: `student-bookings-${user.id}-${Date.now()}`,
            signal
          });
          
          // Fetch bookings where this student is an invited member
          console.log('🔍 Fetching group invites for student:', user.id);
          const invitedBookingsPromise = pb.collection('group_booking_invites').getFullList({
            filter: `claimed_by_user_id="${user.id}"`,
            requestKey: `student-invites-${user.id}-${Date.now()}`,
          }).then(async (invites) => {
            console.log('📋 Found group invites (all):', invites.length);
            
            // Filter for claimed invites
            const claimedInvites = invites.filter(invite => invite.is_claimed);
            console.log('📋 Claimed invites:', claimedInvites.length);
            console.log('📋 Claimed invite data:', claimedInvites);
            
            // Get booking IDs
            const bookingIds = claimedInvites
              .map(invite => invite.booking_id)
              .filter(Boolean);
            
            if (bookingIds.length === 0) {
              console.log('📋 No booking IDs found');
              return [];
            }
            
            console.log('📋 Fetching bookings for IDs:', bookingIds);
            
            // Fetch each booking with full expand
            const bookingPromises = bookingIds.map(id => 
              pb.collection('bookings').getOne(id, {
                expand: 'studentId,tutorId'
              }).catch(err => {
                console.error('❌ Error fetching booking:', id, err);
                return null;
              })
            );
            
            const expandedBookings = (await Promise.all(bookingPromises)).filter(Boolean);
            console.log('📋 Expanded bookings from invites:', expandedBookings);
            return expandedBookings;
          }).catch(err => {
            console.error('⚠️ Could not fetch group invites (permission issue?):', err);
            console.error('⚠️ Error details:', {
              status: err.status,
              message: err.message,
              data: err.data
            });
            // Return empty array if group invites aren't accessible
            return [];
          });
          
          const [leaderBookings, invitedBookings] = await Promise.all([
            leaderBookingsPromise,
            invitedBookingsPromise
          ]);
          
          console.log('📊 Leader bookings:', leaderBookings.items.length);
          console.log('📊 Invited bookings:', invitedBookings.length);
          console.log('📊 Leader booking IDs:', leaderBookings.items.map(b => b.id));
          console.log('📊 Invited booking IDs:', invitedBookings.map(b => b.id));
          
          // Combine bookings (deduplicate by ID)
          const allBookings = [...leaderBookings.items, ...invitedBookings];
          const uniqueBookingIds = new Set();
          bookings = allBookings.filter(booking => {
            if (uniqueBookingIds.has(booking.id)) {
              return false;
            }
            uniqueBookingIds.add(booking.id);
            return true;
          });
          
          console.log('📊 Total unique bookings:', bookings.length);
          console.log('📊 Final booking list:', bookings.map(b => ({ id: b.id, subject: b.subject, date: b.date })));
        }
        
        // For all group bookings, fetch the group members
        const bookingsWithMembers = await Promise.all(
          bookings.map(async (booking) => {
            if (booking.is_group_booking) {
              try {
                const invites = await pb.collection('group_booking_invites').getFullList({
                  filter: `booking_id="${booking.id}"`,
                  expand: 'student_id'
                });
                
                return {
                  ...booking,
                  groupMembers: invites.map(invite => invite.expand?.student_id).filter(Boolean)
                };
              } catch (error) {
                console.error('Error fetching group members for booking:', booking.id, error);
                return booking;
              }
            }
            return booking;
          })
        );
        
        return bookingsWithMembers;
      } catch (error: any) {
        // Handle autocancellation errors gracefully
        if (error?.message?.includes('autocancelled') || error?.name === 'AbortError' || signal?.aborted) {
          return [];
        }
        
        // Handle 404 errors specifically
        if (error?.status === 404) {
          return [];
        }
        
        console.error('useBookings: Error fetching bookings:', error);
        console.error('useBookings: Error details:', {
          message: error.message,
          status: error.status,
          data: error.data
        });
        return [];
      }
    },
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 30000,
    gcTime: 1000 * 60 * 5,
    retry: (failureCount, error: any) => {
      // Don't retry autocancelled requests or 404s
      if (error?.message?.includes('autocancelled') || 
          error?.name === 'AbortError' || 
          error?.status === 404) {
        return false;
      }
      return failureCount < 1;
    },
  });

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};